import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FullSequenceId } from 'src/lib/ActionSequence';
import { captureGenericError } from 'src/lib/sentryCapture';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { State } from '../replicant/State';
import { getLocale } from '../replicant/getters/user';
import type { PopupID } from 'src/game/components/popups/popupRegistry';
import type { PopupDialogOrigin } from 'src/lib/popups/popupOpenClose';

export type SceneID =
  | 'debut'
  | 'livingRoom'
  | 'friendsLivingRoom'
  | 'diningRoom'
  | 'toiletRoom'
  | 'bedroom'
  | 'bathroom'
  | 'arena'
  | '';

// 'left' has become a computed based state, checkable with isSceneLeft()
export type TransitionPhase = 'entering' | 'entered' | 'leaving';

export type Transition = {
  previousScene: SceneID;
  nextScene: SceneID;
  phase: TransitionPhase;
};

export type PopupOpts = {
  id: PopupID;
  enabled: boolean;
  opts: any;
  origin: PopupDialogOrigin | undefined;
  additionalTracking?: Record<string, any>;
};

const initialState = {
  loading: 0,
  screenSize: {
    width: 0,
    height: 0,
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  popupCount: undefined as number | undefined,

  locale: 'ja',

  animating: false,

  // Locks menu actions
  lockMenu: false,

  isPetCrateOpening: false,

  togglePopup: undefined as PopupOpts | undefined,

  transition: {
    previousScene: '' as SceneID,
    nextScene: '' as SceneID,
    phase: 'entered' as TransitionPhase,
  },

  actionSequenceWorking: false,
  actionSequenceId: '' as FullSequenceId,

  stack: '',
  stackRoot: '',
  stackPopups: '',
  lastPopupClosedTime: 0,
  currentPopup: '',
  visibleStats: '',
  previousVisibleStats: '' as string,
  showHeader: true,
  showFooter: true,

  flags: {} as Record<string, boolean>,
};

const slice = createSlice({
  name: 'ui',

  initialState,
  reducers: {
    setScreenSize: (
      state,
      {
        payload,
      }: PayloadAction<{
        width: number;
        height: number;
        top: number;
        bottom: number;
        left: number;
        right: number;
      }>,
    ) => {
      state.screenSize = payload;
    },

    showLocale: (state, { payload }: PayloadAction<string>) => {
      state.locale = payload;
    },
    startSceneTransition: (state, { payload }: PayloadAction<SceneID>) => {
      const { previousScene, nextScene, phase } = state.transition;

      // Cannot start a transition while something is animating
      if (state.animating) return;

      // Cannot start a transition to the same scene we currently are
      if (nextScene === payload) return;

      // Cannot start a transition while another transition is already in place,
      // unless we are trying to reload the scene
      const isReload =
        nextScene === '' && previousScene === payload && phase === 'leaving';

      if (!isReload && phase !== 'entered') {
        throw new Error(
          `Cannot start a transition while another transition is already in place. Previous scene: ${previousScene}. Phase: ${phase}. Next scene: '${nextScene}'. Payload: ${payload}`,
        );
      }

      state.transition.previousScene = nextScene;
      state.transition.nextScene = payload;
      state.transition.phase = 'leaving';

      // UI breadcrumb stack; used exclusively for purchase analytics
      state.stack = payload;
      state.stackRoot = payload;
    },

    setTransitionPhase: (
      state,
      { payload }: PayloadAction<Exclude<TransitionPhase, 'leaving'>>,
    ) => {
      state.transition.phase = payload;
    },

    overwritePreviousScene: (state, { payload }: PayloadAction<SceneID>) => {
      state.transition.previousScene = payload;
    },

    startPopupCount: (state) => {
      state.popupCount = 0;
    },

    stopPopupCount: (state) => {
      delete state.popupCount;
    },

    togglePopup: (state, { payload }: PayloadAction<PopupOpts>) => {
      state.togglePopup = payload;

      // UI breadcrumb stack; used exclusively for purchase analytics
      const popupId = payload.id;
      const popupName = '.' + popupId;

      if (payload.enabled) {
        if (state.popupCount !== undefined) {
          state.popupCount++;
        }

        // We don't have a popup hierarchy system
        // When a popup closes, the other one opens right after
        // We determine hierarchy by timing which is kinda hacky
        if (Date.now() - state.lastPopupClosedTime < 250) {
          state.stack = state.stackRoot + state.stackPopups;
        } else {
          state.stackPopups = '';
        }

        // Add breadcrumb
        state.stack += popupName;

        state.currentPopup = popupId;
      } else {
        // Remove breadcrumb
        const stack = state.stack.split('.');
        const lastPopup = stack.pop();
        if (lastPopup === popupId) {
          state.stack = stack.join('.');
        } else {
          // Something went wrong, reset stack.
          state.stack = state.stackRoot;
        }

        // Set current popup to the top of the stack.
        state.currentPopup = stack.pop() ?? '';

        // Save last popup in case we need it later
        state.lastPopupClosedTime = Date.now();
        if (state.stackPopups.indexOf(popupName) === -1) {
          state.stackPopups += popupName;
        }
      }
    },

    setAnimating: (state, { payload }: PayloadAction<boolean>) => {
      state.animating = payload;
    },

    // Lock menu
    setLockMenu: (state, { payload }: PayloadAction<boolean>) => {
      state.lockMenu = payload;
    },

    showLoading: (state) => {
      ++state.loading;
    },
    hideLoading: (state) => {
      --state.loading;

      // Should never be less than 0, report to Sentry.
      if (state.loading < 0) {
        state.loading = 0;
        captureGenericError('Invalid loading state', null);
      }
    },

    setActionSequenceWorking: (
      state,
      {
        payload,
      }: PayloadAction<
        | { working: true; sequenceId: FullSequenceId }
        | { working: false; sequenceId?: undefined }
      >,
    ) => {
      state.actionSequenceWorking = payload.working;
      state.actionSequenceId = payload.sequenceId!;
    },

    setVisibleStats: (state, { payload }: PayloadAction<string>) => {
      if (state.previousVisibleStats) return;
      state.visibleStats = payload;
    },

    setTempVisibleStats: (state, { payload }: PayloadAction<string>) => {
      if (state.previousVisibleStats) return;
      state.previousVisibleStats = state.visibleStats;
      state.visibleStats = payload;
    },

    clearTempVisibleStats: (state) => {
      if (!state.previousVisibleStats) return;
      state.visibleStats = state.previousVisibleStats;
      state.previousVisibleStats = '';
    },

    setPetCrateOpening: (state, { payload }: PayloadAction<boolean>) => {
      state.isPetCrateOpening = payload;
    },

    setHudVisibility: (
      state,
      { payload }: PayloadAction<{ header?: boolean; footer?: boolean }>,
    ) => {
      state.showHeader = payload.header ?? state.showHeader;
      state.showFooter = payload.footer ?? state.showFooter;
    },

    setFlag: (
      state,
      { payload }: PayloadAction<{ key: string; value: boolean }>,
    ) => {
      state.flags[payload.key] = payload.value;
    },
  },

  extraReducers: (builder) => {
    builder.addCase('RESET_APP', (state, action) => ({
      ...initialState,
      visibleStats: platform.playerID,
      locale:
        'payload' in action ? getLocale(action.payload as State) : state.locale,
      // Keep loading counter.
      loading: state.loading,
    }));
  },
});

export const {
  showLocale,

  setScreenSize,

  startSceneTransition,
  setTransitionPhase,
  overwritePreviousScene,

  togglePopup,

  startPopupCount,
  stopPopupCount,

  setAnimating,
  showLoading,
  hideLoading,
  setVisibleStats,
  setTempVisibleStats,
  clearTempVisibleStats,

  setPetCrateOpening,

  setActionSequenceWorking,
  setLockMenu,

  setHudVisibility,

  setFlag,
} = slice.actions;
export default slice.reducer;
